/** @jsx React.createElement */
import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import axiosInstance, { API_GET } from "../config/axios";
import toast from "react-hot-toast";
import { persistUserProfile } from "../utils/userIdentity";

export const AppContext = React.createContext();

// Utiliser l'instance axios configurée
const API_URL = API_GET;

// Liste des routes publiques où on ne doit pas vérifier l'authentification
const publicRoutes = [
  '/login',
  '/register',
  '/reset-password',
  '/reset-password-employee',
  '/activate-account',
  '/password-fallback',
  '/login-webauthn'
];

const ENTREPRISE_MODULES_SYNC_KEY = 'secura:entreprise-modules-sync';

export const AppContextProvider = (props) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);
  const [userData, setUserData] = useState(null);
  const [interimStatus, setInterimStatus] = useState({ isInterim: false, departmentId: null });
  const [cardUpdateTimestamp, setCardUpdateTimestamp] = useState(Date.now());
  const location = useLocation();
  
  // Vérifier si la route actuelle est publique (location.pathname ne contient pas les query params)
  const isPublic = publicRoutes.includes(location.pathname);

  const getAuthState = async () => {
    // Ne pas vérifier l'authentification sur les routes publiques
    if (isPublic) {
      console.log('ℹ️ Route publique détectée:', location.pathname, '- skip de la vérification d\'authentification');
      return;
    }

    try {
      // Vérifier si un token existe dans localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('ℹ️ Aucun token trouvé, utilisateur non authentifié');
        setIsLoggedIn(false);
        setIsRegistered(false);
        setUserData(null);
        setInterimStatus({ isInterim: false, departmentId: null });
        return;
      }
      
      console.log('🔍 Vérification de l\'authentification avec token:', token.substring(0, 20) + '...');
      const { data } = await axiosInstance.get('/auth/is-auth');
      if (data.success) {
        setIsLoggedIn(true);
        setIsRegistered(true);
        // Charger les données utilisateur après vérification de l'authentification
        // skipAuthCheck=true car on sait déjà que l'utilisateur est authentifié
        await getUserData(true);
      } else {
        setIsLoggedIn(false);
        setIsRegistered(false);
        setUserData(null);
        setInterimStatus({ isInterim: false, departmentId: null });
        console.log('Not authenticated');
      }
    } catch (error) {
      setIsLoggedIn(false);
      setIsRegistered(false);
      setUserData(null);
      setInterimStatus({ isInterim: false, departmentId: null });
      // Ne pas afficher d'erreur si c'est juste une absence de token (401)
      // C'est normal sur les pages publiques
      if (error.response?.status !== 401) {
        console.log('Auth check failed:', error.message);
      }
      // Ne pas logger les 401, c'est normal si l'utilisateur n'est pas connecté
    }
  };

  const getUserData = async (skipAuthCheck = false, forceCardUpdate = false) => {
    try {
      const { data } = await axiosInstance.get('/user/data');
      if (data.success) {
        // Debug temporaire pour vérifier cardImageUrl
        if (data.userData?.cardImageUrl) {
          console.log('✅ AppContext - cardImageUrl trouvé:', data.userData.cardImageUrl);
          console.log('✅ AppContext - Type:', typeof data.userData.cardImageUrl);
        } else {
          console.warn('⚠️ AppContext - cardImageUrl MANQUANT dans userData');
          console.warn('⚠️ AppContext - userData complet:', data.userData);
        }
        setUserData(data.userData);
        persistUserProfile(data.userData);
        // Charger le statut intérim (congés) pour afficher les options responsable pendant la période d'intérim
        loadInterimStatus();
        // Si la carte a été mise à jour, incrémenter le timestamp pour forcer le rechargement
        if (forceCardUpdate) {
          setCardUpdateTimestamp(Date.now());
          console.log('🔄 Timestamp de carte mis à jour:', Date.now());
        }
        return data.userData;
      } else {
        console.warn('Get user data failed:', data.message);
        // Ne pas afficher de toast pour les erreurs attendues
      }
    } catch (error) {
      // Gérer l'erreur 401 (Unauthorized) de manière silencieuse
      if (error.response?.status === 401) {
        if (!skipAuthCheck) {
          console.log('User not authenticated, resetting state');
          setIsLoggedIn(false);
          setIsRegistered(false);
          setUserData(null);
          setInterimStatus({ isInterim: false, departmentId: null });
        }
      } else if (error.response?.status === 404) {
        console.error('❌ Route /user/data non trouvée (404)');
        console.error('❌ URL complète:', `${API_URL}/user/data`);
        console.error('❌ Vérifiez que le serveur est démarré et que la route est bien enregistrée');
        if (!skipAuthCheck) {
          setIsLoggedIn(false);
          setIsRegistered(false);
          setUserData(null);
          setInterimStatus({ isInterim: false, departmentId: null });
        }
      } else {
        console.error('Get user data failed:', error.message);
        console.error('❌ Détails erreur:', {
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          baseURL: error.config?.baseURL
        });
      }
    }
  };

  // Charger le statut intérim (utilisateur est-il intérimaire pour un congé approuvé en cours ?)
  // Accepte camelCase (isInterim, departmentId) ou snake_case (is_interim, department_id) selon l'API
  const loadInterimStatus = async () => {
    try {
      const { data } = await axiosInstance.get('/api/leave-requests/interim-status');
      const isInterim = data?.isInterim === true || data?.is_interim === true;
      const departmentId = data?.departmentId ?? data?.department_id ?? null;
      if (data?.success && isInterim && departmentId != null) {
        setInterimStatus({ isInterim: true, departmentId });
      } else {
        setInterimStatus({ isInterim: false, departmentId: null });
      }
    } catch (e) {
      setInterimStatus({ isInterim: false, departmentId: null });
    }
  };

  // Fonction pour forcer la mise à jour de la carte
  const refreshCard = () => {
    setCardUpdateTimestamp(Date.now());
    getUserData(true, true);
  };

  useEffect(() => {
    // Re-vérifier l'authentification quand la route change
    // Mais seulement si ce n'est pas une route publique
    getAuthState();
  }, [location.pathname]);

  const getUserDataRef = useRef(getUserData);
  getUserDataRef.current = getUserData;

  useEffect(() => {
    const maybeReloadEmployee = (companyId) => {
      if (companyId == null || !userData?.entreprise_id) return;
      if (String(companyId) !== String(userData.entreprise_id)) return;
      getUserDataRef.current(true);
    };

    const onCustom = (e) => maybeReloadEmployee(e.detail?.companyId);
    const onStorage = (e) => {
      if (e.key !== ENTREPRISE_MODULES_SYNC_KEY || !e.newValue) return;
      try {
        const payload = JSON.parse(e.newValue);
        maybeReloadEmployee(payload.companyId);
      } catch {
        /* ignore */
      }
    };

    window.addEventListener('entrepriseCompanyUpdated', onCustom);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('entrepriseCompanyUpdated', onCustom);
      window.removeEventListener('storage', onStorage);
    };
  }, [userData?.entreprise_id]);

  // Note: getUserData est maintenant appelé directement dans getAuthState après vérification
  // pour éviter les appels multiples et les erreurs 401

  const value = {
    isLoggedIn,
    setIsLoggedIn,
    isRegistered,
    setIsRegistered,
    userData,
    setUserData,
    getUserData,
    getAuthState,
    backendUrl: API_URL,
    cardUpdateTimestamp,
    refreshCard,
    interimStatus,
    loadInterimStatus
  };

  return (
    <AppContext.Provider value={value}>
      {props.children}
    </AppContext.Provider>
  );
};

export default AppContext;





























